import { devices } from "@/utils/breakpoints"
import { assetUrl } from "@/utils/cdn"
import HoverPopup from "@/utils/hover"

import React, {
	MouseEventHandler,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react"
import styled, { css } from "styled-components"
import { VideoContext } from "@/hooks/video-provider"

const PlayContainer = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	z-index: 15;

	width: 100%;
	height: 100%;

	display: flex;
	align-items: center;
	justify-content: center;

	user-select: none;
	-webkit-touch-callout: none;
`

const PlayButton = styled.img<{ show: boolean }>`
	width: 44px;
	height: 44px;

	filter: drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.5));

	transition: opacity 300ms ease-in-out;

	${(props) =>
		!props.show &&
		css`
			opacity: 0 !important;
		`}
`

const PlayHover = styled.div<{ enabled: boolean }>`
	padding: 8px;
	background: rgba(0, 0, 0, 0);

	${(props) =>
		props.enabled &&
		css`
			cursor: pointer;
			&:hover {
				background: rgba(0, 0, 0, 0.2);
			}
		`};

	border-radius: 100%;
	transition: background 200ms ease-in-out;
`

export const PlayWithProvider = () => {
	const player = useContext(VideoContext)
	if (player === null) return <></>

	const { paused, play, pause } = player

	return (
		<PlayContainer onClick={paused ? play : pause}>
			<PlayHover enabled={paused}>
				<PlayButton
					src={assetUrl("/icons/player/play.png")}
					show={paused}
				/>
			</PlayHover>
		</PlayContainer>
	)
}

const TimelineContainer = styled.div<{ radius?: string }>`
	width: 100%;
	height: 2px;

	background: rgba(0, 0, 0, 0.4);

	@media ${devices.desktop} {
		height: 44px;

		${(props) =>
			props.radius
				? css`
						border-radius: ${props.radius};
				  `
				: css`
						border-radius: 0 0 20px 20px;
				  `}

		display: flex;
		flex-direction: row;

		gap: 16px;

		align-items: center;

		padding: 0 16px;
	}
`

export const Timeline = (props: { radius?: string }) => {
	const player = useContext(VideoContext)

	if (player === null) return <></>

	return (
		<TimelineContainer radius={props.radius}>
			<Progress />
			<Timestamp />
			<Volume />
		</TimelineContainer>
	)
}

const ProgressWrapper = styled.div`
	flex: 1;

	cursor: pointer;

	&:active {
		cursor: grabbing;
	}

	@media ${devices.desktop} {
		padding: 20px 0;
	}
`

const ProgressContainer = styled.div`
	position: relative;
	width: 100%;
	height: 2px;

	background: rgba(255, 255, 255, 0.2);

	overflow: hidden;

	@media ${devices.desktop} {
		height: 4px;
		border-radius: 8px;
	}
`

const updateInterval = 300

const Hoverbar = styled.div.attrs<{ progress: number }>(
	(props) => ({
		style: {
			width: props.progress + "%",
		},
	}),
)<{ progress: number }>`
	position: absolute;
	z-index: 9;
	top: 0;
	left: 0;

	height: 4px;

	border-radius: 4px;

	background: var(--color-white);
	opacity: 0.2;
`

const Progressbar = styled.div.attrs<{
	progress: number
}>((props) => ({
	style: {
		width: props.progress + "%",
	},
}))<{ progress: number }>`
	position: absolute;
	z-index: 10;
	top: 0;
	left: 0;

	height: 2px;

	transition: width ${updateInterval}ms linear;

	background: #ecedef;

	@media ${devices.desktop} {
		border-radius: 4px;
		height: 4px;
	}
`

const Progress = () => {
	const [hoverProgress, setHoverProgress] = useState(0)

	const player = useContext(VideoContext)

	const ref = useRef<HTMLDivElement>(null)

	const calculateProgress = (mouse: number) => {
		const rect = ref.current?.getBoundingClientRect()
		if (!rect) return 0

		const x = rect.left
		const w = rect.width
		return (mouse - x) / w
	}

	type MoveHandlerReturnsNumber = (
		e: React.MouseEvent,
	) => number
	const handleMoveCallback: MoveHandlerReturnsNumber = (
		e,
	): number => {
		const progress = calculateProgress(e.clientX)

		if (progress > 0) {
			setHoverProgress(progress * 100)
		}

		return progress
	}

	const handleMove = useCallback(handleMoveCallback, [])

	const handleClick: MouseEventHandler = useCallback(
		(e) => {
			if (player === null) return

			const newProgress = handleMove(e)

			player.seek(newProgress * player.duration)
			setHoverProgress(0)
		},
		[player, handleMove],
	)

	if (player === null) return <></>

	const { now, duration } = player
	const progress = (now / duration) * 100

	return (
		<ProgressWrapper
			onClick={handleClick}
			onPointerMove={handleMove}
			ref={ref}>
			<ProgressContainer>
				<Progressbar progress={progress} />
				<Hoverbar progress={hoverProgress} />
			</ProgressContainer>
		</ProgressWrapper>
	)
}

const TimestampText = styled.span`
	font-weight: 500;
	font-size: 12px;
	line-height: 18px;

	text-align: right;
	width: 80px;

	display: none;

	@media ${devices.desktop} {
		display: block;
	}
`

const prettyTime = (seconds: number): string => {
	const roundedSeconds = Math.floor(seconds)
	const remainder = roundedSeconds % 60
	const minutes = Math.floor(roundedSeconds / 60)

	return (
		minutes.toFixed(0).padStart(2, "0") +
		":" +
		remainder.toFixed(0).padStart(2, "0")
	)
}

const Timestamp = () => {
	const player = useContext(VideoContext)

	if (player === null) return <></>

	const { now, duration } = player

	if (!duration) return <></>

	const time =
		prettyTime(now) + " / " + prettyTime(duration)

	return <TimestampText>{time}</TimestampText>
}

const VolumeIcon = styled.img`
	width: 20px;
	height: 20px;

	position: absolute;
	z-index: 70;
	top: 10px;
	left: 10px;
`

const VolumeStepIcon = styled(VolumeIcon)<{
	opacity: number
}>`
	opacity: ${(props) => props.opacity};

	transition: opacity 100ms ease-in-out;
`

const VolumeControlWrapper = styled.div`
	position: absolute;
	top: -190px;
	right: -54px;

	padding: 22px;
`

const VolumeBarForeground = styled.div<{ volume: number }>`
	height: ${(props) => props.volume * 78}px;
	background-color: var(--color-primary-500);
	border-radius: 4px;

	width: 4px;
`

const VolumeBarBackground = styled.div`
	background: rgba(255, 255, 255, 0.4);
	border-radius: 4px;
	width: 4px;

	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	align-items: center;
`

const VolumeBarBall = styled.div`
	width: 8px;
	height: 8px;

	margin: -4px -2px;
	background-color: #ecedef;
	border-radius: 100%;

	z-index: 100;
`

const VolumeControlContainer = styled.div`
	display: flex;
	align-items: stretch;
	justify-content: center;

	cursor: pointer;

	padding: 12px 0;

	width: 28px;
	height: 102px;
	background: rgba(0, 0, 0, 0.8);
	border-radius: 4px;

	&:active {
		cursor: grabbing;
	}

	&:hover ${VolumeBarForeground} {
		width: 6px;
	}

	&:hover ${VolumeBarBall} {
		width: 10px;
		height: 10px;
	}
`

const VolumeButton = styled.div`
	position: relative;
	padding: 20px;

	cursor: pointer;

	flex-shrink: 0;

	width: 40px;
	height: 40px;

	display: none;

	@media ${devices.desktop} {
		display: block;
	}
`

const Volume = () => {
	const [holding, setHolding] = useState(false)

	const [base, setBase] = useState(false)
	const [floating, setFloating] = useState(false)

	const player = useContext(VideoContext)
	const ref = useRef<HTMLDivElement>(null)
	const volumeRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleMouseUp = () => {
			if (holding) {
				setHolding(false)
			}
		}
		addEventListener("mouseup", handleMouseUp)
		return () =>
			removeEventListener("mouseup", handleMouseUp)
	}, [holding, setHolding])

	const handleMove: MouseEventHandler = useCallback(
		(e) => {
			if (player === null) return
			if (holding) {
				player.muted && player.mute(false)
				player.setVolume(calculateVolume(e.clientY))
			}
		},
		[player, holding],
	)

	if (player === null) return <></>

	const { muted, mute, volume, setVolume } = player

	const calculateVolume = (mouse: number) => {
		const rect = volumeRef.current?.getBoundingClientRect()
		if (!rect) return 0

		const y = rect.top
		const h = rect.height
		const result = 2 - (h - y + mouse) / h

		if (result > 1) return 1
		if (result < 0.05) return 0
		return result
	}

	const noSound = volume < 0.05 || muted

	const highOpacity = volume < 0.5 ? 0 : (volume - 0.5) * 2
	const mediumOpacity = volume > 0.5 ? 1 : volume * 2

	return (
		<div>
			<VolumeButton onClick={() => mute(!muted)} ref={ref}>
				{noSound ? (
					<VolumeIcon
						src={assetUrl("/icons/player/volume-slash.svg")}
					/>
				) : (
					<>
						<VolumeIcon
							src={assetUrl("/icons/player/speaker.svg")}
						/>
						<VolumeStepIcon
							opacity={highOpacity}
							src={assetUrl(
								"/icons/player/volume-high.svg",
							)}
						/>
						<VolumeStepIcon
							opacity={mediumOpacity}
							src={assetUrl(
								"/icons/player/volume-medium.svg",
							)}
						/>
					</>
				)}
			</VolumeButton>
			<HoverPopup
				sibling={ref}
				{...{
					base,
					setBase,
					floating,
				}}>
				<VolumeControlWrapper
					onMouseEnter={() => base && setFloating(true)}
					onMouseLeave={() => setFloating(false)}>
					<VolumeControlContainer
						onPointerDown={(e) => {
							setHolding(true)
							muted && mute(false)
							setVolume(calculateVolume(e.clientY))
						}}
						onPointerMove={handleMove}>
						<VolumeBarBackground ref={volumeRef}>
							<VolumeBarBall />
							<VolumeBarForeground volume={volume} />
						</VolumeBarBackground>
					</VolumeControlContainer>
				</VolumeControlWrapper>
			</HoverPopup>
		</div>
	)
}
